import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';

const SUPPORTED_LOCALES = new Set([
  'en', 'es', 'fr', 'zh-CN', 'ja', 'ko', 'pt-BR', 'pt-PT', 'de', 'ru', 'it', 'nl', 'ar',
]);

const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur']);

// Resolve the initial language BEFORE i18next.init runs. Reading the URL
// param up-front lets us hand the right `lng` to init directly, avoiding the
// race where the wrapped changeLanguage / async resource load could lose
// against a later setter (e.g. localizationService, journey-stage hydration).
const URL_LANG_ALIASES: Record<string, string> = {
  zh: 'zh-CN',
  pt: 'pt-PT',
  'pt-br': 'pt-BR',
  'zh-cn': 'zh-CN',
};

function resolveInitialLang(): string {
  try {
    if (typeof window === 'undefined' || !window.location?.search) return 'en';
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get('lng') || params.get('lang') || '').trim();
    if (!raw) return 'en';
    const candidate = URL_LANG_ALIASES[raw.toLowerCase()] || raw;
    return SUPPORTED_LOCALES.has(candidate) ? candidate : 'en';
  } catch (_) {
    return 'en';
  }
}

const INITIAL_LANG = resolveInitialLang();

// When the URL param explicitly specifies a language, that's the user's
// intent for this session. Downstream paths (UserContext.fetchJourneyStage
// applying the backend's preferred_language) check this flag and skip the
// override so the SPA stays in the URL-supplied language even after auth /
// journey-stage hydration completes.
export const wasLanguageSetByUrl = INITIAL_LANG !== 'en';

// Normalize an arbitrary locale string (URL param, host postMessage, or API
// code) to a supported locale, applying the same aliases used at boot.
// Returns null when the input is empty or unsupported.
export function normalizeLocale(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const candidate = URL_LANG_ALIASES[trimmed.toLowerCase()] || trimmed;
  return SUPPORTED_LOCALES.has(candidate) ? candidate : null;
}

// True when the embedding host drove the language for this session — either
// the URL `?lng=` param at boot, or a runtime `cordiant:set-language` message
// from the marketing parent. While set, UserContext must NOT override the
// language with the backend's persisted `preferred_language`: the host's
// explicit choice wins for the session.
let _languageSetByHost = wasLanguageSetByUrl;
export function markLanguageSetByHost(): void {
  _languageSetByHost = true;
}
export function isLanguageSetByHost(): boolean {
  return _languageSetByHost;
}

// Initial `lng` is always 'en' because the only inline resource bundle is
// the English JSON; if we hand init a non-English `lng`, every t() call
// would fall back to English anyway until the locale JSON loads. Below we
// kick off the locale load and switch in one shot, AFTER the bundle is
// registered — so the user sees a brief English flash, then full translation.
i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  debug: import.meta.env.DEV,
});

const inflight = new Map<string, Promise<void>>();

async function loadLocale(lang: string): Promise<void> {
  if (!SUPPORTED_LOCALES.has(lang)) return;
  if (i18n.hasResourceBundle(lang, 'translation')) return;
  const existing = inflight.get(lang);
  if (existing) return existing;
  const p = import(`./locales/${lang}.json`).then((mod) => {
    i18n.addResourceBundle(lang, 'translation', mod.default, true, true);
  }).finally(() => {
    inflight.delete(lang);
  });
  inflight.set(lang, p);
  return p;
}

const applyDocumentDirection = (lang: string) => {
  const base = (lang || 'en').split('-')[0].toLowerCase();
  document.documentElement.lang = lang;
  document.documentElement.dir = RTL_LANGUAGES.has(base) ? 'rtl' : 'ltr';
};

i18n.on('languageChanged', (lang) => {
  applyDocumentDirection(lang);
});

const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (async (lang?: string, ...rest: any[]) => {
  if (lang && lang !== 'en') {
    await loadLocale(lang);
  }
  return originalChangeLanguage(lang as any, ...rest);
}) as typeof i18n.changeLanguage;

applyDocumentDirection('en');

try { localStorage.removeItem('preferred-language'); } catch {}

// Honor `?lng=<locale>` from the URL on first load. The wrapped
// changeLanguage above already loads the locale JSON bundle before switching,
// so this is a single shot — no eager bundle-load needed.
if (INITIAL_LANG !== 'en') {
  void i18n.changeLanguage(INITIAL_LANG);
}

export default i18n;
